import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Status} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {
  createEventWebpageFlow,
  emailMarketingCampaignFlow,
  setErrorMessageForPublishingPanel,
  setPublishingOptionStats,
  setShowLoader,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import type {PublishingOptionStats} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {OrderDetailsForRedux} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import type {SocialMediaResponse, WebLinkResponse} from '@Panels/poster-publishing-options-panel/components/publishing-social-media-panel/publishing-social-media-panel.types';
import type {PublishingOptionLoadingKey} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {PublishingOptionsList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import type {DigitalSignageProviderSubFlow} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import {isPublishingOption} from '@Panels/poster-publishing-options-panel/publishing-options-grid-library';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {isUserLoggedIn, isRevampedPublishDialogEnabled, isUserPAYG} from '@Libraries/user.library';
import {noop} from '@Utils/general.util';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {getEventWizardURLWithDesignPrefill} from '@Libraries/events.library';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {fetchPageDataInBulk} from '@Panels/design-page-selector/design-page-selector-library';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';

export const MIN_SECONDS_TO_SHOW_EMAIL_OPTION = 10;
export const MAX_ELAPSED_TIME_LENGTH = 3;

/** One-shot guard so the page-load stats prefetch fires at most once per page load. */
let havePublishingOptionStatsBeenRequested = false;

export const showErrorOnPanel = (text: string): void => {
  window.PMW.redux.store.dispatch(setErrorMessageForPublishingPanel(text));
};

export const startPreviewGenerationForSavedEditorPoster = (): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster || poster.savePoster.hasUnsavedChanges()) {
    return;
  }

  poster.posterPreview.triggerGeneratePreview();
};

export const getDefaultOrderDetails = (): OrderDetailsForRedux => {
  return {
    progressPercentage: 5,
    estimatedTimeInSeconds: '',
    timeElapsedInSeconds: 0,
    error: '',
  };
};

export const showLoaderForUngeneratedOrders = (product: PosterProductDataForProcessDesign): void => {
  if (product?.status !== Status.DOWNLOADABLE) {
    window.PMW.redux.store.dispatch(setShowLoader(true));
  }
};

/**
 * Fire-and-forget record of one publish-option click for Recommended ranking, gated to logged-in rollout visitors here (not per call site) so every caller stays consistent.
 */
export const incrementPublishingOptionUsage = (option: PublishingOptionsList, signageProvider?: DigitalSignageProviderSubFlow): void => {
  if (!isUserLoggedIn() || !isRevampedPublishDialogEnabled()) {
    return;
  }

  void window.PMW.writeLocal('user/incrementPublishingOptionUsage', {option, ...(signageProvider === undefined ? {} : {signageProvider})}).catch(noop);
};

/**
 * Loads the Recommended row's click counts once per page load (idempotent), so the row's order is settled before the dialog ever opens.
 */
export const fetchPublishingOptionStatsOnPageLoad = (): void => {
  if (havePublishingOptionStatsBeenRequested || !isUserLoggedIn() || !isRevampedPublishDialogEnabled()) {
    return;
  }
  havePublishingOptionStatsBeenRequested = true;

  void window.PMW.readLocal('user/getPublishingOptionStats')
    .then((response) => {
      const stats = (response as {publishingOptionStats: PublishingOptionStats | null}).publishingOptionStats ?? null;
      window.PMW.redux.store.dispatch(setPublishingOptionStats(stats));
    })
    .catch(noop);
};

/** GA4 source for an event created from a design in the publish dialog. */
const EVENT_CREATE_GA_SOURCE = 'My designs';

/**
 * Sends this design into the event wizard, prefilled. A design with more than one page detours through
 * the dialog's page picker first, since the wizard prefills from a single page.
 */
export const startEventWebpageFlowForDesign = (posterHashedId: string): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK, {[GA4EventParamName.SOURCE]: EVENT_CREATE_GA_SOURCE});

  if (!window.PMW.enableMyStuffMultipagesUI) {
    window.location.href = getEventWizardURLWithDesignPrefill(posterHashedId, MediaTypes.MY_DESIGNS);
    return;
  }

  void fetchPageDataInBulk(posterHashedId, true).then((pageData) => {
    if (!pageData || pageData.pageIds.length <= 1) {
      window.location.href = getEventWizardURLWithDesignPrefill(posterHashedId, MediaTypes.MY_DESIGNS, pageData?.pageIds[0]);
      return;
    }
    window.PMW.redux.store.dispatch(createEventWebpageFlow());
  });
};

/**
 * Sends this design into the email-campaign wizard. Email campaigns are subscriber-only, so a PAYG user
 * gets the upsell instead — and the dialog closes first, because the upsell takes over the screen.
 * `closeDialog` must be the panel's own close so this works in the dropdown as well as the modal.
 */
export const startEmailCampaignFlowForDesign = (posterHashedId: string, closeDialog: VoidFunction): void => {
  if (isUserPAYG()) {
    closeDialog();
    openUpsellingDialogForEmailCampaigns();
    return;
  }

  if (window.PMW.enableMyStuffMultipagesUI) {
    window.PMW.redux.store.dispatch(emailMarketingCampaignFlow());
    return;
  }

  window.location.href = window.PMW.util.site_url(`emailmarketing/publish/design/${posterHashedId}`);
};

const isSocialPlatform = (value: string): value is SocialPlatform => {
  const platforms: readonly string[] = Object.values(SocialPlatform);
  return platforms.includes(value);
};

/**
 * Maps a clicked share-grid cell to the counter it bumps (social cells to "Post to socials"); an unrecognized key counts nothing rather than silently inflating a counter.
 */
export const getTrackedPublishingOptionForClick = (loadingKey: PublishingOptionLoadingKey): PublishingOptionsList | null => {
  if (loadingKey === PublishingOptionsList.DIGITAL_SIGNAGE) {
    return null;
  }
  if (isPublishingOption(loadingKey)) {
    return loadingKey;
  }
  if (isSocialPlatform(loadingKey)) {
    return PublishingOptionsList.SOCIAL_MEDIA;
  }
  return null;
};

export function getDefaultPosterProductDataForProcessDesign(): PosterProductDataForProcessDesign {
  return {
    id: 0,
    name: '',
    description: '',
    rawPrice: 0,
    price: '',
    productPrice: 0,
    daysFree: 0,
    premiumContentAdded: false,
    retriesLeft: 0,
    creditsLeft: 0,
    productCreditPrice: 0,
    extraInfo: [],
    galleryTemplateHash: null,
    status: Status.UNDEFINED,
    orderId: '',
    purchaseId: null,
    encryptedPurchaseId: '',
    isWebDownload: false,
    orderFileURLs: [],
    downloadUrl: '',
    downloadFileUrl: '',
    isFree: false,
    hashedId: '',
    posterId: '',
    isVideo: false,
    remainingCredits: 0,
    format: '',
    maxDimension: '',
    height: 0,
    width: 0,
    hasWatermark: false,
    hasClickableLinks: false,
    isLargeSize: false,
    posterRenderId: '',
    iconClassName: '',
    needsPurchase: false,
    posterName: '',
    mimeType: '',
    needsRegenerationDueToDeletion: false,
    existingPageOrders: {},
  };
}

export function getDefaultSocialMediaLinks(): SocialMediaResponse {
  return {
    fb: '',
    tw: '',
    pt: '',
    ptUrl: '',
    posterName: '',
  };
}

export function getDefaultWebLinks(): WebLinkResponse {
  return {
    shareableUrl: '',
    isPublishedLink: false,
    isQrHostedLinkPublished: false,
    qrHostedShareableUrl: '',
    isUrlDisabled: false,
    qrTemplateHashedId: '',
    hasUserSeenQrOption: true,
    publishingCap: null,
    embedPublishingCap: null,
  };
}
